<template>
  <div class="page-container reveal">

    <!-- KPI Strip -->
    <div class="kpi-strip mb-5">
      <div class="kpi-tile">
        <div class="kpi-val text-red-500">{{ kpiOpenAlerts }}</div>
        <div class="kpi-label">Alertes ouvertes</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-val text-amber-400">{{ kpiInvestigating }}</div>
        <div class="kpi-label">En enquête</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-val text-red-600">{{ kpiSuspended }}</div>
        <div class="kpi-label">Artisans suspendus</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-val text-amber-500">{{ kpiUnderReview }}</div>
        <div class="kpi-label">Sous surveillance</div>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="fraud-tabs">

      <!-- ── Tab 1 : Alertes ── -->
      <el-tab-pane name="alerts">
        <template #label>
          <span>Alertes</span>
          <el-badge v-if="kpiOpenAlerts > 0" :value="kpiOpenAlerts" type="danger" class="ml-2" />
        </template>
        <FraudAlertsTable
          :alerts="alerts"
          :status-filter="alertStatusFilter"
          :type-filter="alertTypeFilter"
          :is-loading="isLoadingAlerts"
          :mutating-alert-id="isMutatingAlertId"
          :search-query="searchQ"
          :can-resolve="canResolveFraud"
          :can-sanction="canSanctionArtisans"
          @update:statusFilter="alertStatusFilter = $event; loadAlerts()"
          @update:typeFilter="alertTypeFilter = $event"
          @search="(q) => { searchQ = q; loadAlerts() }"
          @refresh="loadAlerts"
          @detail="openDetailDrawer"
          @events="openEventsDrawer"
          @resolve="resolveAlert"
          @sanction-artisan="prefillSanction"
        />
      </el-tab-pane>

      <!-- ── Tab 2 : Analytique ── -->
      <el-tab-pane label="Analytique" name="analytics">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FraudAnalyticsPanel
            :analytics="analytics"
            :is-loading="isLoadingAnalytics"
            @refresh="loadAnalytics"
          />
          <GpsMonitoringCard
            :alerts="alerts"
            :is-loading="isLoadingAlerts"
          />
        </div>
      </el-tab-pane>

      <!-- ── Tab 3 : Politique & Sanctions ── -->
      <el-tab-pane label="Politique & Sanctions" name="policy">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FraudPolicyEditor
            :policy-form="policyForm"
            :is-saving="isSavingPolicy"
            :can-edit="canManageFraudPolicy"
            @update:policyForm="policyForm = $event"
            @save="savePolicy"
          />
          <FraudSanctionForm
            :sanction-form="sanctionForm"
            :is-applying="isApplyingSanction"
            :prefill-artisan-id="sanctionPrefill.artisanId"
            :prefill-artisan-name="sanctionPrefill.artisanName"
            :can-apply="canSanctionArtisans"
            @update:sanctionForm="sanctionForm = $event"
            @apply="applySanction"
          />
        </div>
      </el-tab-pane>

    </el-tabs>

    <!-- Detail Drawer -->
    <el-drawer append-to-body
      v-model="eventsDrawerVisible"
      title="Détail de l'alerte"
      direction="rtl"
      size="480px"
      destroy-on-close
      @close="events = []; selectedAlert = null"
    >
      <FraudAlertDetailDrawer
        :alert="selectedAlert"
        :events="events"
        :can-resolve="canResolveFraud"
        :can-sanction="canSanctionArtisans"
        :is-mutating="!!isMutatingAlertId"
        @resolve="(payload) => { resolveAlert(payload); eventsDrawerVisible = false }"
        @sanction-artisan="prefillSanction"
      />
    </el-drawer>

  </div>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { ElNotification } from 'element-plus'
import { extractApiError } from '../api'
import { alertStatusBadge } from '../utils/labels'
import {
  getFraudAnalytics,
  getFraudPolicy,
  listFraudAlertEvents,
  listFraudAlerts,
  resolveFraudAlert,
  setArtisanSanction,
  updateFraudPolicy
} from '../services/adminApi'
import FraudAlertsTable from './fraud/FraudAlertsTable.vue'
import FraudAlertDetailDrawer from './fraud/FraudAlertDetailDrawer.vue'
import FraudPolicyEditor from './fraud/FraudPolicyEditor.vue'
import FraudSanctionForm from './fraud/FraudSanctionForm.vue'
import FraudAnalyticsPanel from './fraud/FraudAnalyticsPanel.vue'
import GpsMonitoringCard from './fraud/GpsMonitoringCard.vue'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const alerts          = ref([])
const events          = ref([])
const selectedAlertId = ref('')
const selectedAlert   = ref(null)
const searchQ         = ref('')
const alertStatusFilter = ref('')
const alertTypeFilter   = ref('')
const analytics       = ref({})
const activeTab       = ref('alerts')
const eventsDrawerVisible = ref(false)
const sanctionPrefill = ref({ artisanId: '', artisanName: '' })
const { hasPermission } = useAdminPermissions()
const canResolveFraud = computed(() => hasPermission('fraud.resolve.any'))
const canManageFraudPolicy = computed(() => hasPermission('fraud.policy.manage'))
const canSanctionArtisans = computed(() => hasPermission('artisans.sanction.any'))

const isLoadingAlerts    = ref(false)
const isLoadingAnalytics = ref(false)
const isSavingPolicy     = ref(false)
const isApplyingSanction = ref(false)
const isMutatingAlertId  = ref('')

const policyForm = ref({
  // master switches — must be explicit booleans so el-switch doesn't read undefined as OFF
  antifraud_enabled:              true,
  ai_shield_enabled:              true,
  detect_arrival_out_of_zone:     true,
  detect_fake_cancellation:       true,
  ai_shield_price_check:          true,
  ai_shield_photo_check:          true,
  // numeric settings
  gps_update_interval_seconds:        60,
  arrival_distance_meters:            100,
  proximity_alert_distance_meters:    50,
  fraud_alert_cooldown_minutes:       30,
  fake_cancellation_min_stay_minutes: 5,
  fake_cancellation_distance_meters:  100,
  review_risk_score:                  3.0,
  suspend_risk_score:                 6.0,
  suspend_confirmed_alerts:           2,
  weight_fake_cancellation:           3.0,
  weight_proximity_no_mission:        0.2,
  weight_arrival_out_of_zone:         2.0,
  ai_provider:                        'claude',
  price_benchmark_threshold:          0.3,
  proximity_min_duration_minutes:     15,
  risk_score_half_life_days:          180,
  risk_score_confirmed_half_life_days: 365,
})
const sanctionForm = ref({ artisanId: '', state: 'under_review', reason: '' })

// KPIs derived from loaded data
const kpiOpenAlerts    = computed(() => alerts.value.filter(a => a.status === 'open').length)
const kpiInvestigating = computed(() => alerts.value.filter(a => a.status === 'investigating').length)
const kpiSuspended     = computed(() => (analytics.value?.sanctions || []).find(s => s.state === 'suspended')?.count ?? 0)
const kpiUnderReview   = computed(() => (analytics.value?.sanctions || []).find(s => s.state === 'under_review')?.count ?? 0)

const short  = (value) => String(value || '').slice(0, 8)
const notify = (type, title, message) => ElNotification({ type, title, message, duration: 4500 })

const loadAlerts = async () => {
  isLoadingAlerts.value = true
  try {
    alerts.value = await listFraudAlerts(searchQ.value, alertStatusFilter.value, alertTypeFilter.value)
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  } finally {
    isLoadingAlerts.value = false
  }
}

const resolveAlert = async ({ id, status }) => {
  if (!canResolveFraud.value) return
  isMutatingAlertId.value = id
  try {
    await resolveFraudAlert(id, {
      status,
      reason_code: status === 'dismissed' ? 'false_positive' : 'verified_evidence',
      admin_notes: `Résolution manuelle: ${status}`,
    })
    notify('success', `Alerte #${short(id).toUpperCase()}`, `Statut mis à jour → ${alertStatusBadge(status).label}`)
    await loadAlerts()
    if (eventsDrawerVisible.value && selectedAlertId.value === id) await loadEvents(id)
    if (status === 'confirmed') {
      const alert = alerts.value.find(a => a.id === id)
      if (alert?.artisan_id) {
        notify('warning', 'Fraude confirmée', `Allez dans Politique & Sanctions pour sanctionner ${alert.artisan_name || 'cet artisan'}.`)
      }
    }
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  } finally {
    isMutatingAlertId.value = ''
  }
}

const openDetailDrawer = async (alert) => {
  selectedAlert.value   = alert
  selectedAlertId.value = alert.id
  eventsDrawerVisible.value = true
  await loadEvents(alert.id)
}

const openEventsDrawer = async (alertId) => {
  const alert = alerts.value.find(a => a.id === alertId)
  if (alert) return openDetailDrawer(alert)
  selectedAlertId.value = alertId
  eventsDrawerVisible.value = true
  await loadEvents(alertId)
}

const loadEvents = async (alertId) => {
  try {
    events.value = await listFraudAlertEvents(alertId)
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  }
}

const prefillSanction = ({ artisanId, artisanName }) => {
  if (!canSanctionArtisans.value) return
  sanctionPrefill.value = { artisanId, artisanName }
  sanctionForm.value = { ...sanctionForm.value, artisanId }
  activeTab.value = 'policy'
}

const loadPolicy = async () => {
  try {
    const policy = await getFraudPolicy()
    policyForm.value = {
      // master switches — read explicitly so the UI reflects actual server state
      antifraud_enabled:              policy.antifraud_enabled              ?? true,
      ai_shield_enabled:              policy.ai_shield_enabled              ?? true,
      detect_arrival_out_of_zone:     policy.detect_arrival_out_of_zone     ?? true,
      detect_fake_cancellation:       policy.detect_fake_cancellation       ?? true,
      ai_shield_price_check:          policy.ai_shield_price_check          ?? true,
      ai_shield_photo_check:          policy.ai_shield_photo_check          ?? true,
      // numeric settings
      gps_update_interval_seconds:           policy.gps_update_interval_seconds        ?? 60,
      arrival_distance_meters:               policy.arrival_distance_meters            ?? 100,
      proximity_alert_distance_meters:       policy.proximity_alert_distance_meters    ?? 50,
      fraud_alert_cooldown_minutes:          policy.fraud_alert_cooldown_minutes       ?? 30,
      fake_cancellation_min_stay_minutes:    policy.fake_cancellation_min_stay_minutes ?? 5,
      fake_cancellation_distance_meters:     policy.fake_cancellation_distance_meters  ?? 100,
      review_risk_score:                     policy.review_risk_score                  ?? 3.0,
      suspend_risk_score:                    policy.suspend_risk_score                 ?? 6.0,
      suspend_confirmed_alerts:              policy.suspend_confirmed_alerts           ?? 2,
      weight_fake_cancellation:              policy.weight_fake_cancellation           ?? 3.0,
      weight_proximity_no_mission:           policy.weight_proximity_no_mission        ?? 0.2,
      weight_arrival_out_of_zone:            policy.weight_arrival_out_of_zone         ?? 2.0,
      ai_provider:                           policy.ai_provider                        ?? 'claude',
      price_benchmark_threshold:             policy.price_benchmark_threshold          ?? 0.3,
      proximity_min_duration_minutes:        policy.proximity_min_duration_minutes     ?? 15,
      risk_score_half_life_days:             policy.risk_score_half_life_days          ?? 180,
      risk_score_confirmed_half_life_days:   policy.risk_score_confirmed_half_life_days ?? 365,
    }
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  }
}

const savePolicy = async () => {
  if (!canManageFraudPolicy.value) return
  isSavingPolicy.value = true
  try {
    await updateFraudPolicy(policyForm.value)
    notify('success', 'Politique sauvegardée', 'La configuration antifraude a été déployée.')
    await loadPolicy()
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  } finally {
    isSavingPolicy.value = false
  }
}

const applySanction = async () => {
  if (!canSanctionArtisans.value) return
  isApplyingSanction.value = true
  try {
    await setArtisanSanction(sanctionForm.value.artisanId, {
      state:  sanctionForm.value.state,
      reason: sanctionForm.value.reason || null,
    })
    notify('success', 'Sanction appliquée', `Artisan #${short(sanctionForm.value.artisanId).toUpperCase()} mis à jour.`)
    sanctionForm.value  = { artisanId: '', state: 'under_review', reason: '' }
    sanctionPrefill.value = { artisanId: '', artisanName: '' }
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  } finally {
    isApplyingSanction.value = false
  }
}

const loadAnalytics = async () => {
  isLoadingAnalytics.value = true
  try {
    analytics.value = await getFraudAnalytics()
  } catch (error) {
    notify('error', 'Erreur', extractApiError(error))
  } finally {
    isLoadingAnalytics.value = false
  }
}

watch(alertTypeFilter, () => loadAlerts())

onMounted(async () => {
  await Promise.all([loadAlerts(), loadPolicy(), loadAnalytics()])
})
</script>

<style scoped>
.page-container { @apply h-full overflow-y-auto pb-8; }

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

@media (max-width: 640px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
}

.kpi-tile {
  @apply rounded-xl p-4 border border-profissa-glass-border bg-white/5 text-center;
}

.kpi-val {
  font-size: 1.75rem;
  font-weight: 900;
  line-height: 1;
  font-family: var(--font-display, sans-serif);
}

.kpi-label {
  @apply text-xs font-bold text-profissa-text-muted mt-1 uppercase tracking-wide;
}

:deep(.fraud-tabs .el-tabs__header) {
  margin-bottom: 1.25rem;
}

:deep(.fraud-tabs .el-tabs__item) {
  font-weight: 700;
  font-size: 0.9rem;
}
</style>
